// apps/web/src/pages/admin/CustomerOps.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Paper, Typography, Chip, TextField, InputAdornment, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Alert,
} from '@mui/material';
import { Search as SearchIcon, WarningAmber, CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { statusStyle } from '../../theme/orderStatus';
import {
  getOpsSummary, getCustomerFunnel, getLiveOrders,
  type OrderStatus, type StuckOrder, type LiveOrder,
} from '../../api/customerOps';

const FEED_STATUSES: (OrderStatus | 'ALL')[] = ['ALL', 'PENDING', 'IN_PROGRESS', 'READY_FOR_PICKUP', 'COMPLETED', 'CANCELLED'];

function KpiCard({ value, label, accent }: { value: React.ReactNode; label: string; accent?: string }) {
  return (
    <Paper sx={{ p: 2.5, flex: '1 1 160px', minWidth: 160, bgcolor: 'background.paper' }}>
      <Typography variant="h4" sx={{ color: accent ?? 'text.headline', fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

function StatusChip({ status }: { status: string }) {
  const s = statusStyle(status);
  return <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 600 }} />;
}

function StuckPanel() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-ops', 'ops'],
    queryFn: getOpsSummary,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  return (
    <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
      <Typography variant="h6" sx={{ color: 'text.headline', mb: 2 }}>
        {t('customerOps.stuck.title')}
      </Typography>
      {isLoading && <CircularProgress size={24} />}
      {isError && <Alert severity="error">{t('customerOps.error')}</Alert>}
      {data && data.stuckOrders.length === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <CheckCircleOutline sx={{ color: '#00A86B' }} />
          <Typography>{t('customerOps.stuck.empty')}</Typography>
        </Box>
      )}
      {data && data.stuckOrders.map((o: StuckOrder) => (
        <Box
          key={o.id}
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, py: 1.5,
            borderLeft: '4px solid', borderColor: o.tier === 'CRITICAL' ? '#C62828' : '#E65100',
            pl: 2, mb: 1, bgcolor: o.tier === 'CRITICAL' ? '#FFF5F5' : '#FFFAF3', borderRadius: 1,
          }}
        >
          <WarningAmber sx={{ color: o.tier === 'CRITICAL' ? '#C62828' : '#E65100' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 600, color: 'text.headline' }}>{o.customerName}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {o.pharmacyChainName} · {o.fulfillmentType}
            </Typography>
          </Box>
          <StatusChip status={o.status} />
          <Typography sx={{ fontWeight: 700, color: o.tier === 'CRITICAL' ? '#C62828' : '#E65100', minWidth: 64, textAlign: 'right' }}>
            {o.ageHours}h
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

function FunnelPanel() {
  const { t } = useTranslation();
  const { data, isError } = useQuery({
    queryKey: ['customer-ops', 'funnel'],
    queryFn: getCustomerFunnel,
    refetchInterval: 60000,
  });

  if (isError) {
    return <Box sx={{ mb: 3 }}><Alert severity="error">{t('customerOps.error')}</Alert></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      <KpiCard value={data?.newSignups7d ?? '—'} label={t('customerOps.funnel.signups7d')} />
      <KpiCard value={`${data?.activationRate ?? 0}%`} label={t('customerOps.funnel.activation')} accent="#0066CC" />
      <KpiCard value={data?.intentGapCount ?? '—'} label={t('customerOps.funnel.intentGap')} accent="#E65100" />
      <KpiCard value={data?.searchToOrderRatio ?? '—'} label={t('customerOps.funnel.searchRatio')} />
      <KpiCard value={data?.newSignups30d ?? '—'} label={t('customerOps.funnel.signups30d')} />
    </Box>
  );
}

function LiveFeedPanel() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<OrderStatus | 'ALL'>('ALL');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-ops', 'live', status, search],
    queryFn: () => getLiveOrders({ status: status === 'ALL' ? undefined : status, search: search || undefined }),
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  return (
    <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.headline' }}>{t('customerOps.feed.title')}</Typography>
        <TextField
          size="small"
          placeholder={t('customerOps.feed.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment> }}
        />
      </Box>
      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2 }}>
        {FEED_STATUSES.map((s) => <Tab key={s} value={s} label={s === 'ALL' ? t('customerOps.feed.all') : statusStyle(s).label} />)}
      </Tabs>
      {isLoading && <CircularProgress size={24} />}
      {isError && <Alert severity="error">{t('customerOps.error')}</Alert>}
      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('customerOps.feed.customer')}</TableCell>
              <TableCell>{t('customerOps.feed.chain')}</TableCell>
              <TableCell>{t('customerOps.feed.type')}</TableCell>
              <TableCell>{t('customerOps.feed.status')}</TableCell>
              <TableCell align="right">{t('customerOps.feed.items')}</TableCell>
              <TableCell align="right">{t('customerOps.feed.age')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography sx={{ color: 'text.secondary', py: 2 }}>{t('customerOps.feed.empty')}</Typography></TableCell></TableRow>
            )}
            {data.items.map((o: LiveOrder) => (
              <TableRow key={o.id}>
                <TableCell>{o.customerName}</TableCell>
                <TableCell>{o.pharmacyChainName}</TableCell>
                <TableCell>{o.fulfillmentType}</TableCell>
                <TableCell><StatusChip status={o.status} /></TableCell>
                <TableCell align="right">{o.itemCount}</TableCell>
                <TableCell align="right">{o.ageMinutes < 60 ? `${o.ageMinutes}m` : `${Math.floor(o.ageMinutes / 60)}h`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export function CustomerOps() {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="h4" sx={{ color: 'text.headline', fontWeight: 700, mb: 3 }}>
        {t('customerOps.title')}
      </Typography>
      <StuckPanel />
      <FunnelPanel />
      <LiveFeedPanel />
    </Box>
  );
}
